<template>
  <div class="container">
    <BaseListHeader title="Seznam zákazníků" />

    <div class="row my-3">
      <div class="col-auto d-flex align-items-center">
        <BaseSearchBox v-model="query.search" placeholder="Vyhledat zákazníka…" />
      </div>

      <div class="col-auto ms-auto">
        <div class="form-group my-3">
          <div class="form-check form-switch">
            <label class="form-check-label" for="deleted_switch">Zobrazit smazané zákazníky</label>
            <input
              id="deleted_switch"
              class="form-check-input"
              type="checkbox"
              role="switch"
              v-model="query.filters.deleted"
            >
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-muted py-3">
      Načítám…
    </div>

    <div v-else>
      <BaseDataTable
        :items="items"
        :get-row-key="(r) => r.id"
        :expanded-keys="expandedKeys"
        :expanded-colspan="8"
        @toggle-expand="toggleExpand"
      >
        <template #head>
          <th style="width: 50px;">
            <button
              class="btn btn-sm btn-outline-danger"
              type="button"
              @click="collapseAll"
              :disabled="!hasExpandedRows"
              title="Sbalit vše"
            >
              <i class="bi bi-chevron-double-up"></i>
            </button>
          </th>
          <th>Jméno</th>
          <th>Email</th>
          <th>Telefon</th>
          <th>IČO</th>
          <th>Ulice</th>
          <th>Město</th>
          <th class="text-center" v-tooltip="'Celkový počet kalkulací / počet smazaných kalkulací'">
            Počet kalkulací
          </th>
        </template>

        <template #row="{ row, isExpanded, toggleExpand }">
          <tr class="align-middle custom-row-height" >
            <td>
              <div class="d-flex justify-content-center">
                <button
                  class="btn btn-outline-secondary rounded-circle p-0"
                  style="width: 18px; height: 18px; display: flex; align-items: center; justify-content: center; border-width: 1px;"
                  type="button"
                  @click.stop="toggleExpand"
                  :title="isExpanded ? 'Sbalit' : 'Zobrazit kalkulace'"
                >
                  <i class="bi" :class="isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'" style="font-size: 10px;"></i>
                </button>
              </div>
            </td>

            <td class="fw-semibold">
              <HighlightText :text="row.name" :query="query.search" />
              <span v-if="row.deleted" class="badge text-bg-danger ms-2">smazaný</span>
            </td>

            <td class="text-muted">
              <HighlightText :text="row.email" :query="query.search" />
            </td>

            <td class="text-muted">
              {{ row.phone }}
            </td>

            <td class="text-muted">
              {{ row.ico }}
            </td>

            <td class="text-muted">
              {{ row.street }}
            </td>

            <td class="text-muted">
              {{ row.city }}{{ row.zipcode ? `, ${row.zipcode}` : "" }}
            </td>

            <td class="text-muted text-center">
              {{ row.calculations_count }} / <span class="text-danger">{{ row.deleted_calculations_count }}</span>
            </td>
          </tr>
        </template>

        <template #expandedRow="{ rowKey }">
          <CustomerCalculationsRow :customer-id="rowKey" :expanded="expandedKeys.has(rowKey)" />
        </template>
      </BaseDataTable>

      <div v-if="items.length === 0" class="text-center py-5 text-muted">
        Žádní zákazníci.
      </div>

      <UniversalPagination
        v-if="items.length > 0"
        :current-page="query.page"
        :total-pages="pagination.total_pages || 1"
        :per-page="query.perPage"
        :per-page-options="perPageOptions"
        per-page-label="Položek na stránku"
        @page-changed="(p) => (query.page = p)"
        @per-page-changed="(pp) => (query.perPage = pp)"
      />
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, computed } from "vue";
import api from "@/api/api";
import { useServerList } from "@/composables/useServerList";

import BaseListHeader from "@/components/base/BaseListHeader.vue";
import BaseSearchBox from "@/components/base/BaseSearchBox.vue";
import BaseDataTable from "@/components/base/BaseDataTable.vue";
import UniversalPagination from "@/components/base/UniversalPagination.vue";
import CustomerCalculationsRow from "@/components/customers/CustomerCalculationsRow.vue";
import HighlightText from "@/components/base/HighlightText.vue";

const perPageOptions = ref([10, 20, 50, 100, 500]);
const expandedKeys = ref(new Set());

const hasExpandedRows = computed(() => expandedKeys.value.size > 0);

const collapseAll = () => {
  expandedKeys.value = new Set();
};

const fetchCustomers = async ({ page, perPage, search, filters }) => {
  return await api.customerListPaginated({
    page,
    per_page: perPage,
    search,
    deleted: filters.deleted,
  });
};

const { items, pagination, loading, query, refresh } = useServerList(fetchCustomers, {
  initialPerPage: 100,
  initialSearch: "",
  initialFilters: { deleted: false },
  searchDebounceMs: 300,
});

const toggleExpand = (key) => {
  const s = new Set(expandedKeys.value);
  if (s.has(key)) s.delete(key);
  else s.add(key);
  expandedKeys.value = s;
};

onMounted(() => {
  refresh();
});
</script>

<style scoped>
.custom-row-height {
  height: 50px; /* Nebo jiná požadovaná výška */
}
</style>